<template>
  <section class="content-header">
    <h1>Importación de autofacturas de compañías</h1>
  </section>

  <section class="content p-4">
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">Fichero a importar</h3>
      </div>
      <div class="box-body">
        <p class="text-muted">
          Selecciona la compañía y su fichero de autofacturas. Cada página se adjunta
          al expediente que le corresponda; las que no se puedan asociar se listan abajo.
        </p>
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label for="compania">Compañía</label>
              <Dropdown v-model="companiaSeleccionada" inputId="compania"
                :options="companias" optionLabel="nombre" optionValue="idcompañia"
                filter placeholder="Selecciona una compañía" class="w-100" />
            </div>
          </div>
          <div class="col-md-5">
            <div class="form-group">
              <label for="pdf">PDF de autofacturas</label>
              <input type="file" id="pdf" accept="application/pdf" class="form-control" ref="inputPdf" @change="seleccionarArchivo">
            </div>
          </div>
        </div>
      </div>
      <div class="box-footer">
        <button class="btn btn-primary" @click="importar()" :disabled="cargando">
          <i class="fa" :class="cargando ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
          Analizar e importar
        </button>
      </div>
    </div>

    <!-- TABLA RESULTADOS -->
    <div class="box box-primary mt-5">
      
      <div class="box-header with-border d-flex">
        <h3 class="box-title">{{ titulo }}</h3>
        <select class="custom-select form-control-sm ml-5" style="width:auto;" v-model="filtroEstado" @change="cargarListado()">
          <option v-for="e in estados" :key="e.valor" :value="e.valor">{{ e.etiqueta }}</option>
        </select>
      </div>
      <div class="box-body mt-2">
        <DataTable :value="pendientes" v-model:selection="seleccionadas" dataKey="id_autofactura"
                   :loading="cargandoListado" :paginator="true" :rows="15"
                   :rowsPerPageOptions="[15,25,50]" stripedRows responsiveLayout="scroll"
                   currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                   paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown">

          <template #empty>No hay autofacturas pendientes de enviar.</template>

          <Column selectionMode="multiple" headerStyle="width:3rem"></Column>

          <Column field="codigo" header="Expediente" sortable></Column>

          <Column header="Cliente" sortable field="nombre">
            <template #body="s">
              <span :title="datosContacto(s.data)">{{ s.data.nombre || '—' }}</span>
              <i v-if="discrepaNombre(s.data)" class="fas fa-exclamation-triangle text-warning"
                 style="margin-left:6px" :title="'En la factura de la compañía figura: ' + s.data.nombre_cliente"></i>
            </template>
          </Column>

          <Column header="Email" field="email">
            <template #body="s">
              <span v-if="s.data.email">{{ s.data.email }}</span>
              <span v-else class="text-danger">sin email</span>
            </template>
          </Column>

          <Column header="Factura compañía" field="referencia_factura" sortable></Column>

          <Column header="Importe" field="importe_total" sortable bodyStyle="text-align:right">
            <template #body="s">{{ s.data.importe_total }} €</template>
          </Column>

          <Column header="Autofactura" bodyStyle="text-align:center">
            <template #body="s">
              <button type="button" class="btn btn-default btn-xs" title="Ver la factura de la compañía" @click="abrirPdf(s.data.url_autofactura)">
                <i class="fas fa-file-pdf"></i>
              </button>
            </template>
          </Column>

          <Column header="Registro a enviar">
            <template #body="s">
              <span v-if="nombreFacturaElegida(s.data)">{{ nombreFacturaElegida(s.data) }}</span>
              <span v-else-if="s.data.coincidencias > 1" class="text-warning">
                {{ s.data.coincidencias }} coinciden en importe
              </span>
              <span v-else-if="s.data.n_facturas == 0" class="text-danger">sin facturas</span>
              <span v-else class="text-muted">ninguna cuadra</span>

              <button type="button" class="btn btn-default btn-xs" style="margin-left:6px"
                      :disabled="s.data.n_facturas == 0" title="Elegir el registro"
                      @click="abrirFacturas(s.data)">
                <i class="fas fa-list"></i>
              </button>
            </template>
          </Column>

          <Column header="Enviar" bodyStyle="text-align:center">
            <template #body="s">
              <button type="button" class="btn btn-primary btn-xs"
                      :disabled="!puedeEnviar(s.data) || enviando"
                      :title="motivoNoEnviar(s.data)"
                      @click="enviarFilas([s.data])">
                <i class="fas fa-paper-plane"></i>
              </button>
            </template>
          </Column>
        </DataTable>
      </div>
      <div class="box-footer">
        <button class="btn btn-primary" :disabled="!seleccionadas.length || enviando" @click="enviarFilas(seleccionadas)">
          <i class="fa" :class="enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
          Enviar seleccionadas ({{ seleccionadas.length }})
        </button>
      </div>
    </div>

    <Dialog modal header="Facturas del expediente" v-model:visible="dialogoFacturas"
            :style="{ width: '40rem' }">
      <table class="table table-sm table-striped" v-if="filaActiva">
        <thead>
          <tr><th>Nº</th><th>Fecha</th><th class="text-right">Importe</th><th></th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="f in filaActiva.facturas" :key="f.id_factura">
            <td>{{ f.numero_factura }}</td>
            <td>{{ f.fecha_factura }}</td>
            <td class="text-right" :class="{ 'text-success': Math.abs(f.total_factura - filaActiva.importe_total) < 0.01 }">
              {{ f.total_factura }} €
            </td>
            <td>
              <button type="button" class="btn btn-default btn-xs" @click="abrirPdf(f.url_completa_factura_pdf)">
                <i class="fas fa-file-pdf"></i> Ver
              </button>
            </td>
            <td>
              <button type="button" class="btn btn-primary btn-xs" @click="elegirFactura(f.id_factura)">
                Elegir
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="text-muted">
        La factura de la compañía es de <strong>{{ filaActiva ? filaActiva.importe_total : '' }} €</strong>.
        Las que coinciden salen en verde.
      </p>
    </Dialog>
  </section>
</template>
<script>
import { PwgsApi } from '../../../services/PwgsApi';
import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';

export default {
  components: { Dropdown, DataTable, Column, Dialog },

  data() {
    return {
      companias: [],
      companiaSeleccionada: null,
      archivo: null,
      cargando: false,
      resultado: null,
      pendientes: [],
      seleccionadas: [],
      facturaElegida: {},
      dialogoFacturas: false,
      filaActiva: null,
      cargandoListado: false,
      filtroEstado: 'pendientes',
      enviando: false,
      estados: [
        { valor: 'pendientes', etiqueta: 'Pendientes de enviar' },
        { valor: 'enviadas',   etiqueta: 'Ya enviadas' },
        { valor: 'todas',      etiqueta: 'Todas' }
      ],
    };
  },

  methods: {
    async obtenerCompanias() {
        const api = new PwgsApi;
        const resp = await api.get('companias/autofacturas?sortField=nombre&sortOrder=1');        
        this.companias = resp.datos;
    },
    seleccionarArchivo(e) {
      this.archivo = e.target.files.length ? e.target.files[0] : null;
    },
    async importar() {
      if (!this.companiaSeleccionada) {
        this.$toast.add({ severity: 'warn', summary: 'Falta la compañía', detail: 'Selecciona la compañía que emite las autofacturas.', life: 4000 });
        return;
      }
      if (!this.archivo) {
        this.$toast.add({ severity: 'warn', summary: 'Falta el fichero', detail: 'Selecciona el PDF.', life: 4000 });
        return;
      }

      this.cargando = true;
      this.resultado = null;
      const formData = new FormData();
      formData.append('pdf', this.archivo);
      formData.append('idcompanyia', this.companiaSeleccionada);
      const api = new PwgsApi;

      try {
        this.resultado = await api.post('autofacturas', formData);
        const importadas = Number((this.resultado.resumen || {}).importada || 0);
        this.$toast.add({severity: importadas > 0 ? 'success' : 'warn', summary: 'Importación terminada', detail: importadas + ' de ' + this.resultado.n_total_registros + ' facturas adjuntadas a su expediente', life: 5000});
        this.archivo = null;
        if (this.$refs.inputPdf){ 
          this.$refs.inputPdf.value = ''; 
        }
        await this.cargarListado();
      } catch (e) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: e, life: 5000 });
      }
      this.cargando = false;
    },
    etiquetaResultado(clave) {
      const mapa = {
        importada:      'Importada',
        duplicada:      'Ya estaba',
        sin_expediente: 'Sin expediente',
        ambigua:        'Varios expedientes',
        error:          'Error',
        error_lectura:  'No se pudo leer'
      };
      return mapa[clave] || clave;
    },
    claseResultado(clave) {
      const mapa = {
        importada:      'text-success',
        duplicada:      'text-muted',
        sin_expediente: 'text-warning',
        ambigua:        'text-warning',
        error:          'text-danger',
        error_lectura:  'text-danger'
      };
      return mapa[clave] || '';
    },
    async cargarListado() {
      if (!this.companiaSeleccionada){ 
        this.pendientes = []; return;
      }
      this.cargandoListado = true;
      const api = new PwgsApi;
      
      try {
        const resp = await api.get('autofacturas/listado?idcompanyia=' + this.companiaSeleccionada + '&estado=' + this.filtroEstado);
        this.pendientes = resp.datos || [];
        this.seleccionadas = [];

        //Seleccionar sugerencia
        this.facturaElegida = {};
        this.pendientes.forEach(f => {
          if (f.id_factura_sugerida) {
            this.facturaElegida[f.id_autofactura] = f.id_factura_sugerida;
          }
        });
      } catch (e) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: e, life: 5000 });
      }
      this.cargandoListado = false;
    },
    abrirFacturas(fila) {
      this.filaActiva = fila;
      this.dialogoFacturas = true;
    },
    elegirFactura(id_factura) {
      this.facturaElegida[this.filaActiva.id_autofactura] = id_factura;
      this.dialogoFacturas = false;
    },
    nombreFacturaElegida(fila) {
      const id = this.facturaElegida[fila.id_autofactura];
      if (!id) { 
        return null; 
      }
      const f = (fila.facturas || []).find(x => x.id_factura == id);
      return f ? (f.numero_factura + ' · ' + f.total_factura + ' €') : null;
    },
    abrirPdf(url) {
      if (url) { 
        window.open(url, '_blank', 'width=900, height=700'); 
      }
    },
    datosContacto(fila) {
      return [fila.direccion, fila.poblacion, fila.telefono1, fila.telefono2, fila.telefono3]
             .filter(x => x).join(' · ');
    },
    discrepaNombre(fila) {
      if (!fila.nombre || !fila.nombre_cliente){
         return false; 
      }
      return fila.nombre.trim().toLowerCase() !== fila.nombre_cliente.trim().toLowerCase();
    },
    puedeEnviar(fila) {
      return !!fila.email && !!this.facturaElegida[fila.id_autofactura] && !fila.fecha_envio;
    },
    motivoNoEnviar(fila) {
      if (fila.fecha_envio){ 
        return 'Ya enviada el ' + fila.fecha_envio; 
      }
      if (!fila.email){
        return 'El cliente no tiene email'; 
      }
      if (!this.facturaElegida[fila.id_autofactura]){
        return 'Falta elegir el registro a enviar';
      }
      return 'Enviar por email';
    },
    async enviarFilas(filas) {
      const envios = filas
        .filter(f => this.puedeEnviar(f))
        .map(f => ({ id_autofactura: f.id_autofactura, id_factura: this.facturaElegida[f.id_autofactura] }));

      const descartadas = filas.length - envios.length;
      if (!envios.length) {
        this.$toast.add({ severity: 'warn', summary: 'Nada que enviar',
          detail: 'Ninguna de las seleccionadas tiene email y registro.', life: 5000 });
        return;
      }
      if (descartadas > 0 && !confirm('Se van a enviar ' + envios.length + '. Quedan fuera '
          + descartadas + ' por falta de email o de registro. ¿Continuar?')) {
        return;
      }

      this.enviando = true;
      const api = new PwgsApi;
      try {
        const resp = await api.post('autofacturas/enviar', {
          idcompanyia: this.companiaSeleccionada,
          envios: envios
        });
        if (resp.fallidos > 0) {
          const motivos = (resp.datos || []).filter(r => !r.enviado).map(r => r.detalle);
          this.$toast.add({ severity: 'warn', summary: 'Enviadas ' + resp.enviados + ' de ' + resp.n_total_registros,
            detail: motivos.join(' · '), life: 10000 });
        } else {
          this.$toast.add({ severity: 'success', summary: 'Enviadas',
            detail: resp.enviados + ' autofacturas enviadas.', life: 3000 });
        }
        await this.cargarListado();
      } catch (e) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: e, life: 5000 });
      }
      this.enviando = false;
    },
  },
  mounted() {
    this.obtenerCompanias();
  },
  computed: {
    titulo() {
      const e = this.estados.find(x => x.valor === this.filtroEstado);
      return e ? e.etiqueta : '';
    },
  },
  watch: {
    companiaSeleccionada() {
      this.cargarListado();
    }
  }
}
</script>